import React, { useEffect, useState } from "react";
import axios from "axios";
import { X } from "lucide-react";
import { LANDING } from "@/constants/testIds";

const PACKAGE_LABELS = {
  starter: { name: "Starter Package", price: "$297" },
  all_star: { name: "All-Star Package", price: "$497" },
  championship: { name: "Championship Package", price: "$997" },
};

const LeadCaptureModal = ({ open, onClose, defaultPackage = "all_star", apiBase }) => {
  const [pkg, setPkg] = useState(defaultPackage);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [business, setBusiness] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setPkg(defaultPackage);
      setError("");
    }
  }, [open, defaultPackage]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape" && open) onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim() || !email.trim() || !business.trim()) {
      setError("Please fill in all fields.");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError("Please enter a valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${apiBase}/checkout/session`, {
        package_id: pkg,
        name: name.trim(),
        email: email.trim(),
        business: business.trim(),
        origin_url: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        setError("Could not start checkout. Try again.");
        setSubmitting(false);
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail || "Something went wrong. Please try again.";
      setError(typeof msg === "string" ? msg : "Something went wrong.");
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid={LANDING.modalOverlay}
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-md flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-3xl p-7 md:p-9 max-w-md w-full border border-black/10 shadow-2xl relative">
        <button
          type="button"
          onClick={onClose}
          data-testid={LANDING.modalClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-black/5 transition"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        <div className="mb-5">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#EF426F]">
            🏀 Claim Your Finals Spot
          </p>
          <h3 className="mt-2 text-2xl md:text-3xl font-black tracking-tight leading-tight">
            Lock in your{" "}
            <span className="bg-gradient-to-r from-[#00B2A9] via-[#EF426F] to-[#FF8200] text-transparent bg-clip-text">
              {PACKAGE_LABELS[pkg]?.name || "Package"}
            </span>
          </h3>
          <p className="mt-2 text-sm text-zinc-600">
            Quick details, then straight to secure Stripe checkout.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3.5">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-700 mb-1.5">
              Package
            </label>
            <select
              value={pkg}
              onChange={(e) => setPkg(e.target.value)}
              data-testid="lead-modal-package-select"
              className="w-full rounded-xl border border-black/10 px-4 py-3 text-sm font-medium bg-white focus:outline-none focus:ring-2 focus:ring-[#00B2A9]"
            >
              <option value="starter">Starter Package — $297</option>
              <option value="all_star">All-Star Package — $497</option>
              <option value="championship">Championship Package — $997</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-700 mb-1.5">
              Your Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid={LANDING.modalNameInput}
              placeholder="Jane Doe"
              className="w-full rounded-xl border border-black/10 px-4 py-3 text-sm font-medium bg-white focus:outline-none focus:ring-2 focus:ring-[#00B2A9]"
            />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-700 mb-1.5">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid={LANDING.modalEmailInput}
              placeholder="jane@yourbusiness.com"
              className="w-full rounded-xl border border-black/10 px-4 py-3 text-sm font-medium bg-white focus:outline-none focus:ring-2 focus:ring-[#00B2A9]"
            />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-700 mb-1.5">
              Business Name
            </label>
            <input
              type="text"
              value={business}
              onChange={(e) => setBusiness(e.target.value)}
              data-testid={LANDING.modalBusinessInput}
              placeholder="Your Business"
              className="w-full rounded-xl border border-black/10 px-4 py-3 text-sm font-medium bg-white focus:outline-none focus:ring-2 focus:ring-[#00B2A9]"
            />
          </div>

          {error && (
            <p
              data-testid={LANDING.modalError}
              className="text-sm font-semibold text-[#EF426F]"
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid={LANDING.modalSubmit}
            className="w-full mt-2 rounded-full bg-[#0A0A0A] text-white font-bold py-4 text-sm tracking-wider uppercase transition-all hover:shadow-[0_10px_40px_-10px_#EF426F] hover:scale-[1.01] active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting
              ? "Redirecting to checkout…"
              : `🏀 Pay ${PACKAGE_LABELS[pkg]?.price || ""} & Claim Spot`}
          </button>
          <p className="text-[11px] text-center text-zinc-500 pt-1">
            Secure checkout powered by Stripe. No subscriptions.
          </p>
        </form>
      </div>
    </div>
  );
};

export default LeadCaptureModal;
